import React, { useEffect } from 'react';
import I18n from 'i18n-js';
import { Position, Viewer } from '@photo-sphere-viewer/core';
import { MarkersPlugin } from '@photo-sphere-viewer/markers-plugin';
import IPanoramicImage from '@/components/interfaces/PanoramicImage';
import { useDebounceCallback } from 'usehooks-ts';
import usePanoramicImageStore
  from '@/components/admin/SlideShow/Slide/Content/SlideObjectPanoramicImage/PanoramicImage/usePanoramicImageStore';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import {
  hotspotsToMarkers,
} from '@/components/admin/SlideShow/Slide/Content/SlideObjectPanoramicImage/PanoramicImage/PanoramicImage';
import ReactDOM from 'react-dom';
import HiddenHotspots from './HiddenHotspots';
import OVERLAY_ICON from './icons/Hover_Over.svg';

const showInstructionalOverlay = (panoViewer: Viewer, locale?: 'en' | 'es') => {
  panoViewer.overlay.show({
    id: 'pano-instructions',
    image: `<img alt="" src="${OVERLAY_ICON}" />`,
    text: I18n.t('panoramic_image_overlay_text', { locale }),
    title: I18n.t('panoramic_image_overlay_title', { locale }),
  });

  const dismissOverlay = () => {
    const { overlay } = panoViewer;
    overlay.container.style.opacity = '0';
    setTimeout(() => {
      overlay.hide('pano-instructions');
      overlay.container.style.opacity = '';
    }, 150);
  };
  panoViewer.container.addEventListener('pointerdown', dismissOverlay, { once: true });
  panoViewer.parent.addEventListener('keydown', dismissOverlay, { once: true });
};

interface PanoramicImageViewerProps {
  panoramicImage: IPanoramicImage;
  setPanoramicCamera: (pos: Position) => void;
  locale?: 'en' | 'es';
}

const PanoramicImageViewer = ({
  panoramicImage, locale, setPanoramicCamera,
}: PanoramicImageViewerProps) => {
  const markers = panoramicImage.hotspots;
  const viewerId = `pano-${panoramicImage.id}-viewer`;
  const updateDefaultView = useDebounceCallback(setPanoramicCamera, 50);
  const setContainer = usePanoramicImageStore(state => state.setContainer);

  const slideId = useSlideShowContext(state => state.currentSlide.id);

  useEffect(() => {
    const hotspots = markers.map((marker) => {
      const domElement = document.querySelector(`.hotspot-${marker.id}`);

      if (marker.placed && domElement) {
        return ({
          ...marker,
          element: domElement as HTMLElement,
        });
      }

      return null;
    }).filter(Boolean);

    // eslint-disable-next-line no-new
    const panoViewer = new Viewer({
      container: viewerId,
      keyboard: 'always',
      lang: {
        fullscreen: I18n.t('panoramic_image_fullscreen', { locale }),
        zoomIn: I18n.t('panoramic_image_zoom_in', { locale }),
        zoomOut: I18n.t('panoramic_image_zoom_out', { locale }),
      },
      navbar: ['zoomOut', 'zoomIn', 'fullscreen'],
      panorama: panoramicImage.image.url ?? '/missing.png',
      plugins: [
        [MarkersPlugin, { markers: hotspots }],
      ],
    });

    const markersPlugin = panoViewer.getPlugin(MarkersPlugin) as MarkersPlugin;

    showInstructionalOverlay(panoViewer, locale);

    const defaultView = panoramicImage.default_view;
    if (defaultView.yaw && defaultView.pitch) panoViewer.rotate(panoramicImage.default_view);

    // Add content to the <canvas> element for accessibility:
    // https://developer.mozilla.org/en-US/docs/Web/API/Canvas_API/Tutorial/Basic_usage#accessible_content
    panoViewer.addEventListener('ready', () => {
      const { image: { description_en, description_es } } = panoramicImage;
      const description = locale === 'es' ? description_es || description_en : description_en;
      $(`#${viewerId} canvas`).text(description || 'Panoramic image');
    });

    panoViewer.addEventListener('position-updated', ({ position }) => {
      updateDefaultView(position);
    });

    panoViewer.addEventListener('fullscreen', () => {
      const currentSlide = document.getElementById(`${slideId}`);
      if (!currentSlide) return;

      markersPlugin.clearMarkers();

      const container = document.getElementById(`hiddenHotspots${panoramicImage.id}`);
      ReactDOM.unmountComponentAtNode(container);

      ReactDOM.render(
        (<HiddenHotspots panoramicImage={panoramicImage} locale={locale} />),
        container,
      );

      if (document.fullscreenElement && document.fullscreenElement.id.includes('pano')) {
        currentSlide.style.transform = 'none';
      } else {
        window.dispatchEvent(new Event('pano-fullscreen'));
      }
      markersPlugin.setMarkers(hotspotsToMarkers(hotspots));
    });

    setContainer(panoViewer.container);
    return () => panoViewer.destroy();
  }, [markers, slideId]);

  return (
    <>
      <div id={`hiddenHotspots${panoramicImage.id}`} />
      <HiddenHotspots locale={locale} panoramicImage={panoramicImage} />
      <div
        key={viewerId}
        id={viewerId}
        role="application"
        style={{ height: '100%', position: 'relative', width: '100%' }}
        tabIndex={1}
      />
    </>
  );
};

export default PanoramicImageViewer;
